/* eslint-disable jsx-a11y/no-static-element-interactions */
import cn from 'classnames';
import {
  Field, FieldProps, Form, FormikProvider, useFormik,
} from 'formik';
import {
  memo, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
  requestPostVacancyResponse,
  resetErrors,
} from '../../redux/actions/vacancy-page';
import { IDocumentsReducerState } from '../../redux/reducers/documentsReducer';
import { RootState } from '../../redux/reducers/rootReducer';
import { PERSONAL_DATA_POLICY_LINK } from '../../utils/constants/client';
import { onMaskPhoneNumber } from '../../utils/on-mask-phone-number';
import { ResponseSchema } from '../../utils/validation/validation';
import AttachFile from '../AttachFile';
import { CloseWarningModal } from '../CloseWarningModal';
import CustomModal from '../CustomModal';
import FormCheckbox from '../FormCheckbox';
import { ResultModal } from '../ResultModal';
import HeartIcon from '../SVGIcons/HeartIcon';
import SadIcon from '../SVGIcons/SadIcon';
import classes from './response-modal.module.scss';

interface IResponseModalProps {
  vacancy: string | null;
  division: string;
  titleResponse: string;
  closeModal: () => void;
  recommendation?: string | null;
  defaultValues: {
    firstName: string;
    lastName: string;
    patronymic: string;
    birthdate: string;
    phoneNumber: string;
    telegram: string;
    email: string;
    currentWorkplace: string;
    currentJob: string;
    expectedSalary: string;
    coverLetter: string;
    cvFile: File | null;
    checkPersonalData: boolean;
    checkIT: boolean;
  };
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}

export interface IResponseFormValues {
  firstName: string;
  lastName: string;
  patronymic: string;
  birthdate: string;
  phoneNumber: string;
  telegram: string;
  email: string;
  currentWorkplace: string;
  currentJob: string;
  expectedSalary: string;
  coverLetter: string;
  cvFile: File;
  checkPersonalData: boolean;
  checkIT: boolean;
}

function ResponseModal({
  vacancy,
  titleResponse,
  division,
  closeModal,
  recommendation,
  defaultValues,
  isOpen,
  setIsOpen,
}: IResponseModalProps) {
  const isLoading = useSelector<RootState, boolean>(
    (state) => state.vacancyReducer.isSending,
  );
  const responseError = useSelector<RootState, string | undefined>(
    (state) => state.vacancyReducer.responseError,
  );
  const responseSent = useSelector<RootState, boolean>(
    (state) => state.vacancyReducer.responseSent,
  );
  const docs = useSelector<RootState, IDocumentsReducerState>(
    (state) => state.documentsReducer,
  );
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  const resultModalTitle = useMemo(() => {
    if (responseError) {
      return 'Отклик не отправлен,\nпопробуйте снова';
    }
    return 'Спасибо за отклик!';
  }, [responseError]);

  const resultModalSubtitle = useMemo(() => {
    if (responseError) {
      return '';
    }
    return 'Мы свяжемся с тобой в течение\n3-х рабочих дней';
  }, [responseError]);

  const resultModalIcon = useMemo(() => {
    if (responseError) {
      return SadIcon;
    }
    return HeartIcon;
  }, [responseError]);

  const resultModalButtonText = useMemo(() => {
    if (responseError) {
      return 'Вернуться к форме';
    }
    return '';
  }, [responseError]);

  const coverLetterRef = useRef<HTMLTextAreaElement>(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const isWithVacancy = location.pathname.includes('tech')
    || location.pathname.includes('office')
    || location.pathname.includes('operations');

  const formik = useFormik<IResponseModalProps['defaultValues']>({
    initialValues: defaultValues,
    validationSchema: ResponseSchema,
    onSubmit: async (values_) => {
      const values = ResponseSchema.cast(values_) as IResponseFormValues;
      dispatch(
        requestPostVacancyResponse({
          values: {
            ...values,
            recommendation,
            ...(isWithVacancy
              ? { vacancy: null, division }
              : { vacancy, division: null }),
          },
          resetValues: () => null,
        }),
      );
      window._mtm?.push({ event: 'sendCV' });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  } = formik;

  const handleClick = async () => {
    const validateErrors = await formik.validateForm();

    if (Object.keys(validateErrors).length > 0) {
      const firstErrorField = Object.keys(validateErrors)[0];

      const errorElement = document.querySelector<HTMLInputElement>(
        `[name="${firstErrorField}"]`,
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement?.focus();
      }
    }
  };

  const handleCloseModal = () => {
    if (responseError || responseSent) {
      dispatch(resetErrors());
      formik.resetForm();
      closeModal();
      return;
    }
    if (Object.keys(touched).length === 0) {
      closeModal();
      return;
    }
    closeModal();
    setIsCloseModalOpen(true);
  };

  const onChangeBirthdate = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    const onlyDigits = value.replace(/\D/g, '');

    let maskedValue = '';

    if (onlyDigits.length > 0) {
      maskedValue += `${onlyDigits.slice(0, 2)}`;
    }
    if (onlyDigits.length >= 3) {
      maskedValue += `.${onlyDigits.slice(2, 4)}`;
    }
    if (onlyDigits.length >= 5) {
      maskedValue += `.${onlyDigits.slice(4, 8)}`;
    }

    setFieldValue('birthdate', maskedValue);
  };

  const onChangePhoneNumber = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setFieldValue('phoneNumber', onMaskPhoneNumber(value));
  };

  return (
    <>
      <ResultModal
        isOpen={!!responseError || responseSent}
        text={resultModalTitle}
        subtext={resultModalSubtitle}
        icon={resultModalIcon}
        buttonText={resultModalButtonText}
        onButtonClick={() => dispatch(resetErrors())}
        onClose={handleCloseModal}
      />

      <CloseWarningModal
        isOpen={isCloseModalOpen}
        onCancel={() => {
          setIsOpen?.(true);
          setIsCloseModalOpen(false);
        }}
        onOk={() => {
          closeModal();
          formik.resetForm();
          setIsCloseModalOpen(false);
        }}
      />

      <FormikProvider value={formik}>
        <CustomModal.Root
          isOpen={!!isOpen && !responseError && !responseSent}
          onClose={handleCloseModal}
          Paper={Form}
          paperClassName={classes.form}
          paperProps={{
            onSubmit: handleSubmit,
          }}
        >
          <CustomModal.Header>
            <CustomModal.HeaderText>{titleResponse}</CustomModal.HeaderText>
          </CustomModal.Header>

          <CustomModal.Body>
            <h2 className={classes['form__category-title']}>Личные данные</h2>
            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Фамилия*"
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onFocus={() => setFieldError('lastName', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.lastName && touched.lastName,
                      [classes['input--filled']]:
                        !errors.lastName && values.lastName,
                    },
                  )}
                />
                {errors.lastName && touched.lastName && (
                  <div className={classes['form__error-label']}>
                    {errors.lastName}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Имя*"
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onFocus={() => setFieldError('firstName', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.firstName && touched.firstName,
                      [classes['input--filled']]:
                        !errors.firstName && values.firstName,
                    },
                  )}
                />
                {errors.firstName && touched.firstName && (
                  <div className={classes['form__error-label']}>
                    {errors.firstName}
                  </div>
                )}
              </div>
            </div>

            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Отчество"
                  type="text"
                  name="patronymic"
                  value={values.patronymic}
                  onChange={handleChange}
                  onFocus={() => setFieldError('patronymic', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.patronymic && touched.patronymic,
                      [classes['input--filled']]:
                        !errors.patronymic && values.patronymic,
                    },
                  )}
                />
                {errors.patronymic && touched.patronymic && (
                  <div className={classes['form__error-label']}>
                    {errors.patronymic}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Дата рождения*"
                  type="text"
                  name="birthdate"
                  value={values.birthdate}
                  onChange={onChangeBirthdate}
                  onFocus={() => setFieldError('birthdate', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.birthdate && touched.birthdate,
                      [classes['input--filled']]:
                        !errors.birthdate && values.birthdate,
                    },
                  )}
                />
                {values.birthdate && (
                <div className={classes.mask}>
                  <span className={classes.mask__filled}>
                    {values.birthdate}
                  </span>
                  {'01.01.2000'.slice(values.birthdate.length)}
                </div>
                )}
                {errors.birthdate && touched.birthdate && (
                  <div className={classes['form__error-label']}>
                    {errors.birthdate}
                  </div>
                )}
              </div>
            </div>

            <h2 className={classes['form__category-title']}>Контакты</h2>

            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Мобильный телефон*"
                  type="text"
                  name="phoneNumber"
                  value={values.phoneNumber}
                  onChange={onChangePhoneNumber}
                  onFocus={() => setFieldError('phoneNumber', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.phoneNumber && touched.phoneNumber,
                      [classes['input--filled']]:
                        !errors.phoneNumber && values.phoneNumber,
                    },
                  )}
                />
                {values.phoneNumber && (
                <div className={classes.mask}>
                  <span className={classes.mask__filled}>
                    {values.phoneNumber}
                  </span>
                  {'+7 (999) 000 00 00'.slice(values.phoneNumber.length)}
                </div>
                )}
                {errors.phoneNumber && touched.phoneNumber && (
                  <div className={classes['form__error-label']}>
                    {errors.phoneNumber}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Телеграм"
                  type="text"
                  name="telegram"
                  value={values.telegram}
                  onChange={handleChange}
                  onFocus={() => setFieldError('telegram', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.telegram && touched.telegram,
                      [classes['input--filled']]:
                        !errors.telegram && values.telegram,
                    },
                  )}
                />
                {errors.telegram && touched.telegram && (
                  <div className={classes['form__error-label']}>
                    {errors.telegram}
                  </div>
                )}
              </div>
            </div>

            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Email*"
                  type="text"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onFocus={() => setFieldError('email', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]: errors.email && touched.email,
                      [classes['input--filled']]: !errors.email && values.email,
                    },
                  )}
                />
                {errors.email && touched.email && (
                  <div className={classes['form__error-label']}>
                    {errors.email}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                  classes['form__offset-item--desktop-only'],
                )}
              />
            </div>

            <h2 className={classes['form__category-title']}>Опыт и ожидания</h2>

            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Текущее место работы*"
                  type="text"
                  name="currentWorkplace"
                  value={values.currentWorkplace}
                  onChange={handleChange}
                  onFocus={() => setFieldError('currentWorkplace', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.currentWorkplace && touched.currentWorkplace,
                      [classes['input--filled']]:
                        !errors.currentWorkplace && values.currentWorkplace,
                    },
                  )}
                />
                {errors.currentWorkplace && touched.currentWorkplace && (
                  <div className={classes['form__error-label']}>
                    {errors.currentWorkplace}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Текущая должность*"
                  type="text"
                  name="currentJob"
                  value={values.currentJob}
                  onChange={handleChange}
                  onFocus={() => setFieldError('currentJob', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.currentJob && touched.currentJob,
                      [classes['input--filled']]:
                        !errors.currentJob && values.currentJob,
                    },
                  )}
                />
                {errors.currentJob && touched.currentJob && (
                  <div className={classes['form__error-label']}>
                    {errors.currentJob}
                  </div>
                )}
              </div>
            </div>

            <div className={classes['form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                )}
              >
                <Field
                  placeholder="Зарплатные ожидания (₽)"
                  type="text"
                  name="expectedSalary"
                  value={values.expectedSalary}
                  onChange={handleChange}
                  onFocus={() => setFieldError('expectedSalary', undefined)}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.expectedSalary && touched.expectedSalary,
                      [classes['input--filled']]:
                        !errors.expectedSalary && values.expectedSalary,
                    },
                  )}
                />
                {values.expectedSalary && (
                <div className={classes.mask}>
                  <span className={classes.mask__filled}>
                    {values.expectedSalary}
                  </span>
                  {' '}
                  <span className={classes.mask__black}>₽</span>
                </div>
                )}

                {errors.expectedSalary && touched.expectedSalary && (
                  <div className={classes['form__error-label']}>
                    {errors.expectedSalary}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  classes['form__horizontal-grow'],
                  classes['form__offset-item'],
                  classes['form__offset-item--desktop-only'],
                )}
              />
            </div>

            <div
              onMouseDown={(e) => {
                e.preventDefault();
                coverLetterRef.current?.focus();
              }}
              className={cn(
                classes['cover-letter__wrapper'],
                classes['form__offset-item'],
                {
                  [classes['cover-letter__wrapper--filled']]:
                    !errors.coverLetter && values.coverLetter,
                },
              )}
            >
              <Field
                placeholder="Сопроводительное письмо"
                type="text"
                name="coverLetter"
                component="textarea"
                innerRef={coverLetterRef}
                value={values.coverLetter}
                onFocus={() => setFieldError('coverLetter', undefined)}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const el = event.currentTarget;
                  el.style.height = '';
                  el.style.height = `${el.scrollHeight + 4}px`;
                  handleChange(event);
                }}
                onBlur={handleBlur}
                className={cn(
                  classes.input,
                  classes['cover-letter__field'],
                  {
                    [classes['input--error']]:
                      errors.coverLetter && touched.coverLetter,
                    [classes['input--filled']]:
                      !errors.coverLetter && values.coverLetter,
                  },
                )}
              />
            </div>
            {errors.coverLetter && touched.coverLetter && (
              <div className={classes['form__error-label']}>
                {errors.coverLetter}
              </div>
            )}

            <div className={classes['form__offset-item']}>
              <Field
                className={classes['recommend-form__offset-item']}
                name="cvFile"
                as={AttachFile}
                allowedFormats="PDF или WORD"
                maxSize="Не более 24МБ"
                required
                value={values.cvFile}
                onChange={(value: null | File) => {
                  void setFieldTouched('cvFile', true, true).then(
                    () => void setFieldValue('cvFile', value, true),
                  );
                }}
              />
              {touched.cvFile && errors.cvFile && (
                <div
                  className={cn(
                    classes['form__error-label'],
                    classes['form__attach-file-error'],
                  )}
                >
                  {errors.cvFile}
                </div>
              )}
            </div>

            <div className={classes['checkboxes-area']}>
              <div
                className={cn(
                  classes['checkbox-area'],
                  classes['form__first-checkbox'],
                )}
              >
                <Field
                  render={({ field }: FieldProps) => (
                    <FormCheckbox
                      checked={field.value}
                      onChange={() => {
                        void setFieldValue('checkPersonalData', !field.value);
                      }}
                      errors={
                        !!errors.checkPersonalData
                        && !!touched.checkPersonalData
                      }
                    />
                  )}
                  name="checkPersonalData"
                  value={values.checkPersonalData}
                  checked={values.checkPersonalData}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div className={classes['checkbox-area__text']}>
                  <div className={classes['checkbox-area__label']}>
                    Я даю согласие на обработку
                  </div>
                  <a
                    href={String(docs.documentPD)}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className={classes['checkbox-area__label-link']}
                  >
                    персональных данных
                  </a>
                  {' '}
                  и подтверждаю ознакомление с
                  <a
                    href={PERSONAL_DATA_POLICY_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className={classes['checkbox-area__label-link']}
                  >
                    политикой обработки ПДн
                  </a>
                </div>
              </div>

              <div
                className={cn(
                  classes['checkbox-area'],
                  classes['form__offset-checkbox-item'],
                )}
              >
                <Field
                  render={({ field }: FieldProps) => (
                    <FormCheckbox
                      checked={field.value}
                      onChange={() => {
                        void setFieldValue('checkIT', !field.value);
                      }}
                      errors={!!errors.checkIT && !!touched.checkIT}
                    />
                  )}
                  name="checkIT"
                  value={values.checkIT}
                  checked={values.checkIT}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div className={classes['checkbox-area__text']}>
                  <div className={classes['checkbox-area__label']}>
                    Я даю согласие на получение
                  </div>
                  <a
                    href={String(docs.documentIT)}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className={classes['checkbox-area__label-link']}
                  >
                    информационно-развлекательных материалов
                  </a>
                </div>
              </div>
            </div>
          </CustomModal.Body>

          <CustomModal.Footer className={classes.form__footer}>
            <CustomModal.Button
              type="submit"
              isLoading={isLoading}
              onClick={handleClick}
            >
              Отправить
            </CustomModal.Button>
          </CustomModal.Footer>
        </CustomModal.Root>
      </FormikProvider>
    </>
  );
}

export default memo(ResponseModal);
